/**
 * GRABAR UNA NOTA DE VOZ con el micrófono del móvil, como lo hace el asistente
 * del trabajador (mismo orden de formatos —iOS da `audio/mp4`—, mismo tope de
 * 90 s), pero como hook para poder usarlo fuera de él. El asistente conserva su
 * copia inline a propósito: sus guards comprueban esos cuerpos literalmente.
 *
 * `start()` tiene que llamarse DENTRO del gesto de toque (si no, Safari no da el
 * micrófono). Si el móvil se va a segundo plano grabando, la grabación se pierde:
 * `stop()` cierra el micro y devuelve lo que haya.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const MAX_AUDIO_SECONDS = 90;

export function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac"];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* noop */
    }
  }
  return undefined;
}

export function mimeToExt(mime: string | undefined): string {
  if (!mime) return "webm";
  if (mime.includes("mp4") || mime.includes("aac")) return "m4a";
  return "webm";
}

export interface AudioTake {
  blob: Blob;
  mime: string;
  seconds: number;
}

export function useAudioRecorder(onTake: (take: AudioTake) => void, onError?: () => void) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const secondsRef = useRef(0);

  const stop = useCallback(() => {
    try {
      recorderRef.current?.stop();
    } catch {
      /* noop */
    }
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((tk) => tk.stop());
    streamRef.current = null;
    setRecording(false);
  }, []);

  const start = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickRecorderMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size > 0) onTake({ blob, mime: type, seconds: secondsRef.current });
      };
      rec.start();
      recorderRef.current = rec;
      secondsRef.current = 0;
      setSeconds(0);
      setRecording(true);
      timerRef.current = window.setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
        if (secondsRef.current >= MAX_AUDIO_SECONDS) stop();
      }, 1000);
      return true;
    } catch (err) {
      console.error("Mic permission error:", err);
      onError?.();
      return false;
    }
  }, [onTake, onError, stop]);

  // Al desmontar, el micro se apaga; lo grabado hasta entonces llega por onstop.
  useEffect(() => () => stop(), [stop]);

  return { recording, seconds, start, stop };
}
